import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { SeoService } from 'src/app/common/services/seo.service/seo.service';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { IAppState } from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { PlaceDetailsBaseComponent } from '../place.details.base';
import { IPlaceDetailsModalData } from '../place.details.modal.provider';
import { GoogleMapComponent } from 'src/app/modules/shared/components/google.map/google.map.component';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { PlaceVisitorQA, VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { MatMenuTrigger } from '@angular/material/menu';
import { FancyEventDetailsModalComponent } from 'src/app/modules/event-calendar/components/event.details.modal/fancy.event.details.modal/fancy.event.details.modal.component';
import { IEventDetailsModalData } from 'src/app/modules/event-calendar/components/event.details.modal/event.details.modal.provider';
import { TimelineComponent } from 'src/app/modules/event-calendar/components/timeline/timeline.component';
import { NearbyPlacesComponent } from 'src/app/modules/shared/components/nearby.places/nearby.places.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Observable, filter, of, switchMap, tap, timer } from 'rxjs';
import { EventDateFilterComponent } from 'src/app/modules/event-calendar/components/event.date.filter/event.date.filter.component';
import { IMapMarkerWithCustomData } from 'src/app/modules/shared/models/place.marker';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';
import { SUN_EDITOR_CSS_URL } from 'src/app/common/constants/sun.editor';

// @DynamicEncapsulation()
@Component({
  selector: 'ig-fancy-place-details-modal',
  templateUrl: './fancy.place.details.modal.component.html',
  styleUrls: ['./fancy.place.details.modal.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  host: {
    style: 'font-family: Roboto, sans-serif',
  },
})
export class FancyPlaceDetailsModalComponent
  extends PlaceDetailsBaseComponent
  implements OnInit, AfterViewInit
{
  // Inputs for normal component usage
  @Input() currentPlace?: VisitorPlace;
  @Input() placeDetailsId?: string;
  @Input() from?:
    | 'eventCalendar'
    | 'placeCalendar'
    | 'itinerary'
    | 'nearbyPlaces'
    | 'nearbyOnPlace'
    | 'nearbyOnEvent';

  // settings: IFrontendSettings;
  @ViewChild('map', { static: false }) googleMap: GoogleMapComponent;

  @ViewChild('nearbyEventsContainer', { static: false })
  nearbyEventsContainer: ElementRef;
  @ViewChild('card', { static: false }) card: ElementRef;
  @ViewChild('timeline', { static: false }) timeline: TimelineComponent;
  @ViewChild('dateFilter', { static: false })
  dateFilter: EventDateFilterComponent;

  @ViewChild(MatMenuTrigger, { static: false })
  matMenuTrigger: MatMenuTrigger;

  @ViewChild('nearbyPlacesCom', { static: false })
  nearbyPlacesCom: NearbyPlacesComponent;

  isAtStart = true;
  isAtEnd = false;

  nearbyEventsRange = 2;

  nearbyEventsMode: 'normal' | 'timeline' = 'normal';
  range: number = 2;

  isCssReady = false;
  isYoutubeLoaded = false;

  private readonly hostEl = inject(ElementRef<HTMLElement>);

  private static readonly HOST_HEADER_OFFSET_MAX_PX = 96;

  constructor(
    public override activatedRoute: ActivatedRoute,
    public override seoService: SeoService,
    public override errorAlert: ErrorAlertService,
    public override sanitizer: DomSanitizer,
    public override placeService: VisitorPlaceService,
    public override modalService: ModalService,
    public scriptService: ScriptService,
    public cdRef: ChangeDetectorRef,
    @Optional() public override dialogRef: MatDialogRef<FancyPlaceDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) @Optional() public override data: IPlaceDetailsModalData,
    public override _store: Store<IAppState>,
  ) {
    super(
      activatedRoute,
      seoService,
      errorAlert,
      sanitizer,
      placeService,
      modalService,
      dialogRef,
      data,
      _store,
    );

    // this._store.pipe(select(frontendSettingsSelector)).subscribe((settings) => {
    //   settings && (this.settings = settings);
    // });

    // handle browser back button click, close the event details view and reload the page
    window.addEventListener('popstate', this.hashChanged.bind(this));

    // // load the html editor css style so that the customized description style can be loaded

    // as this modal is ShadowDom, we need to inject the styles into it
    dialogRef?.afterOpened().subscribe(() => {
      this.scheduleEmbedScroll();
      // ENHANCED: Now injectStyle automatically applies Wix fixes when cdRef is provided
      modalService.injectStyle(
        '.cdk-overlay-container ig-fancy-place-details-modal',
        [SUN_EDITOR_CSS_URL],
        () => {
          this.isCssReady = true;
          this.cdRef.detectChanges();
          requestAnimationFrame(() => {
            this.resetCurrentDialogScrollers();
            // Back mounts with isCssReady — only re-nudge host scroll for Places embeds.
            if (this.shouldScrollHostForPlaceDetail()) {
              this.ensureBackControlInHostView();
            }
            this.focusBackToPlaces();
          });
        },
        this.cdRef, // Pass ChangeDetectorRef for automatic Wix fixes
        'FancyPlaceDetailsModal', // Component ID for logging
      );
    });

    dialogRef?.beforeClosed().subscribe(() => {
      this.restoreListingCard();
    });

    if (!dialogRef) {
      setTimeout(() => {
        modalService.injectStyle(
          '#imgoing-container ig-fancy-place-details-modal',
          [SUN_EDITOR_CSS_URL],
          () => {
            this.isCssReady = true;

            timer(200)
              .pipe(
                tap(() => {
                  // for some clients use Wix. One upgrade of Wix causes the list not to be updated. Refer to https://itidigital.atlassian.net/browse/DXP-2040
                  this.cdRef.detectChanges();
                }),
              )
              .subscribe();
          },
        );
      }, 200);
    }

    this.range = this.settings.nearbyPlacesDefaultRange;
  }

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe((params) => {
      const placeDetailsId = params.get('placeId');
      console.log('placeId ----', placeDetailsId);
      if (placeDetailsId) {
        this.placeDetailsId = placeDetailsId;
        // If you need to load place details here, you can call the appropriate method
        // this.loadPlaceDetails(placeDetailsId);
      }
    });

    // Use modalData if available (modal mode), otherwise use @Input properties (normal component mode)
    if (this.data) {
      this.currentPlace = this.data.currentPlace;
      this.placeDetailsId = this.data.placeDetailsId;
      this.from = this.data.from;
    } else {
      this.data = {
        currentPlace: this.currentPlace,
        placeDetailsId: this.placeDetailsId,
        from: this.from,
      };
      // this.isCssReady = true;
    }
  }

  ngAfterViewInit(): void {
    // window['CLIENT_APP_MESSAGE'] = {
    //   type: 'CLIENT_APP_MESSAGE_TYPE',
    //   hubName: this.hubName,
    //   userId: '1234567890',
    // };
    // this.socketService.init();
    setTimeout(() => {
      // this.socketService.socket.on('IG_MESSAGE', (res) => {
      //   console.log('IG_MESSAGE', res);
      // });

      try {
        if (
          window['CLIENT_APP_MESSAGE'] &&
          window['CLIENT_APP_MESSAGE']['hubName'] === this.hubName
        ) {
          this.visitorService
            .sendSocketMsg('IG_MESSAGE', {
              type: 'POI_DETAILS_VIEWED',
              poiId: this.place._id,
              poiName: this.place.name,
              ...window['CLIENT_APP_MESSAGE'],
            })
            .subscribe((res) => {});
        }
      } catch (error) {
        console.warn('error when sending socket msg', error);
      }
    });
  }
  // socketService = inject(SocketService);

  loadYoutube() {
    this.isYoutubeLoaded = true;
    this.cdRef.detectChanges();
  }

  get displayingNearbyEvents() {
    if (!this.filteredNearByEvents) {
      return this.nearByEvents;
    }
    return this.filteredNearByEvents;
  }

  get isAccommodation() {
    if (!this.settings) return false;
    return this.placeService.checkIsAccommodation(
      this.place,
      this.settings.placeCategories['Accommodations'],
    );
  }

  get hasGoogleReviews() {
    return (
      !this.place.isManuallyCreated &&
      (this.place?.googleReviews?.filter((x) => x.text && +x.rating >= 3).length ||
        this.place?.reviews?.filter((x) => x.text && +x.rating >= 3).length)
    );
  }

  get displayVisitorQAs(): PlaceVisitorQA[] {
    if (!Array.isArray(this.place?.visitorQAs)) {
      return [];
    }

    return this.place.visitorQAs.filter(
      (qa) => qa?.question?.trim() && qa?.answer?.trim()
    );
  }


  private scheduleEmbedScroll(): void {
    if (!this.dialogRef || this.isProtectedEmbedSurface()) {
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.shouldScrollHostForPlaceDetail()) {
          this.scrollEmbedToDetailTop();
        } else {
          this.resetCurrentDialogScrollers();
        }
      });
    });
  }


  private isProtectedEmbedSurface(): boolean {
    const wrapper = this.getEmbedWrapper();
    const route = `${wrapper?.getAttribute('route') || ''} ${window.location.pathname}`.toLowerCase();
    return (
      route.includes('/one-row') ||
      route.includes('travel-buddy') ||
      route.includes('/trip-planner') ||
      route.includes('/itineraries') ||
      route.includes('/itinerary/')
    );
  }

  private getEmbedWrapper(): HTMLElement | null {
    return (
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix')
    );
  }

  private isEmbeddedFrame(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  private openedFromListing(): boolean {
    const from = this.data?.from;
    return !from || from === 'placeCalendar';
  }


  private shouldScrollHostForPlaceDetail(): boolean {
    if (!this.openedFromListing() || this.isProtectedEmbedSurface()) {
      return false;
    }
    return !!this.getEmbedWrapper();
  }

  private scrollEmbedToDetailTop(): void {
    if (!this.isEmbeddedFrame()) {
      const wrapper = this.getEmbedWrapper();
      // Trip planner and the standalone calendar are not host-page embeds.
      if (!wrapper || wrapper.getBoundingClientRect().top >= 0) {
        this.resetCurrentDialogScrollers();
        this.ensureBackControlInHostView();
        return;
      }
      this.releaseBlockedPageScroll();
      this.scrollHostToEmbedTop(wrapper);
    } else {
      this.scrollFrameToDetailTop();
    }
    this.resetCurrentDialogScrollers();
    this.ensureBackControlInHostView();
  }


  private getHostHeaderOffsetPx(): number {
    return Math.min(
      FancyPlaceDetailsModalComponent.HOST_HEADER_OFFSET_MAX_PX,
      Math.max(0, Math.round(window.innerHeight * 0.12)),
    );
  }


  private scrollHostToEmbedTop(wrapper: HTMLElement): void {
    const headerOffset = this.getHostHeaderOffsetPx();
    const delta = wrapper.getBoundingClientRect().top - headerOffset;
    if (Math.abs(delta) > 1) {
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
    }
  }


  private scrollFrameToDetailTop(): void {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const anchor = this.ensureEmbedScrollAnchor();
    anchor.style.scrollMarginTop = `${this.getHostHeaderOffsetPx()}px`;
    anchor.scrollIntoView({
      block: 'start',
      inline: 'nearest',
      behavior: 'auto',
    });
  }

  private ensureEmbedScrollAnchor(): HTMLElement {
    const existing = document.getElementById('ig-place-detail-scroll-anchor');
    if (existing) {
      return existing;
    }
    const anchor = document.createElement('div');
    anchor.id = 'ig-place-detail-scroll-anchor';
    anchor.setAttribute('aria-hidden', 'true');
    anchor.style.cssText =
      'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;scroll-margin-top:0;';
    document.body.prepend(anchor);
    return anchor;
  }

  private releaseBlockedPageScroll(): void {
    const html = document.documentElement;
    if (!html.classList.contains('cdk-global-scrollblock')) {
      return;
    }
    const lockedTop = Math.abs(parseInt(html.style.top || '0', 10)) || 0;
    html.classList.remove('cdk-global-scrollblock');
    html.style.top = '';
    html.style.left = '';
    window.scrollTo(0, lockedTop);
  }

  private getCurrentOverlayPane(): HTMLElement | null {
    const host = this.hostEl?.nativeElement;
    if (!(host instanceof HTMLElement)) {
      return null;
    }
    return host.closest('.cdk-overlay-pane');
  }

  private getBackControl(): HTMLElement | null {
    const host = this.hostEl?.nativeElement;
    const back = host?.shadowRoot?.querySelector('div.fixed.top-1.cursor-pointer');
    return back instanceof HTMLElement ? back : null;
  }


  private ensureBackControlInHostView(): void {
    const back = this.getBackControl();
    if (!back) {
      return;
    }
    const offset = this.getHostHeaderOffsetPx();
    const rect = back.getBoundingClientRect();
    const needsNudge = rect.bottom <= 0 || rect.top >= window.innerHeight;
    if (!needsNudge) {
      return;
    }
    const prevMargin = back.style.scrollMarginTop;
    back.style.scrollMarginTop = `${offset}px`;
    back.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
    back.style.scrollMarginTop = prevMargin;
  }

  private resetCurrentDialogScrollers(): void {
    if (this.shouldScrollHostForPlaceDetail()) {
      const wrapper = this.getEmbedWrapper();
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }

    if (this.isProtectedEmbedSurface()) {
      return;
    }

    const pane = this.getCurrentOverlayPane();
    if (!pane) {
      return;
    }
    const surface =
      (pane.querySelector('.mat-mdc-dialog-surface') as HTMLElement | null) ||
      (pane.querySelector('.mat-mdc-dialog-container') as HTMLElement | null);
    if (surface) {
      surface.scrollTop = 0;
    }
  }

  private focusBackToPlaces(): void {
    const back = this.getBackControl();
    if (!back) {
      return;
    }
    if (!back.hasAttribute('tabindex')) {
      back.setAttribute('tabindex', '-1');
    }
    back.focus({ preventScroll: true });
  }

  private restoreListingCard(): void {
    if (!this.isEmbeddedFrame() || !this.shouldScrollHostForPlaceDetail()) {
      return;
    }
    const name = `${this.place?.name || this.currentPlace?.name || ''}`.trim();
    const root = document.querySelector('app-root')?.shadowRoot;
    if (!name || !root) {
      return;
    }
    const card = Array.from(root.querySelectorAll('[role="button"]')).find((el) => {
      const text = (el.textContent || '').replace(/\s+/g, ' ');
      return text.includes(name) && `${el.className}`.includes('cursor-pointer');
    });
    if (card instanceof HTMLElement) {
      card.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    }
  }

  getFrom(): string {
    switch (this.data?.from) {
      case 'eventCalendar':
        return 'ALL EVENTS';
      case 'placeCalendar':
        return 'PLACES';
      case 'itinerary':
        return 'ITINERARY';
      case 'nearbyOnEvent':
        return 'EVENT';
      default:
        return 'PLACES';
    }
  }

  public close() {
    this.dialogRef.close();
  }

  public filterEventsByDate(command: { from: Date; to: Date }) {
    // this.filteredNearByEvents$ = this.nearByEvents$.pipe(
    //   switchMap((events) => this.filterEvents(events, command))
    // );
    this.filteredNearByEvents = this.filterEvents(this.nearByEvents, command);
  }

  filterEvents(events, command: { from: Date; to: Date }): VisitorEvent[] {
    const now = new Date();
    let matched: VisitorEvent[] = [];

    events?.forEach((item) => {
      if (!item.eventTimes || !item.eventTimes.length) {
        item.eventTimes = [
          {
            startTime: item.startTime,
            endTime: item.endTime,
          },
        ];
      }

      for (let time of item.eventTimes) {
        const startTime = time.startTime ? new Date(time.startTime) : null;
        const endTime = time.endTime ? new Date(time.endTime) : null;

        const isOngoing = endTime ? endTime >= now : true;
        const isUpcoming = startTime >= now;

        if (!isOngoing || !isUpcoming) {
          continue;
        }

        const isInRange =
          (!command.from && !command.to) ||
          (!endTime && startTime >= command.from && startTime <= command.to) ||
          (endTime && startTime <= command.to && endTime >= command.from) ||
          (endTime && startTime >= command.from && endTime <= command.to);

        if (isInRange) {
          matched.push(item);
          break;
        }
      }
    });

    return matched;
  }

  public goToEvent(event: VisitorEvent) {
    this.modalService.show({
      component: FancyEventDetailsModalComponent,
      panelClass: 'ig-modal-w-full',
      data: {
        currentEvent: event,
        allEvents: [],
        favCustomEvents: [],
        from: 'nearbyOnPlace', //'placeCalendar',
      } as IEventDetailsModalData,
    });
  }

  public setNearbyEventsRange(range: number) {
    this.nearbyEventsRange = range;
    this.filteredNearByEvents = null;
    this.nearByEvents = [];
    this.dateFilter?.clear(false);
    this.isAtEnd = false;
    this.isAtStart = true;
    this.matMenuTrigger?.closeMenu();
    this.loadNearbyEvents(range);
    if (this.isShowingNearbyEventsOnMap) {
      this.toggleShowNearbyEventsOnMap({ checked: true, source: null });
    }
  }

  public checkIsAtStart() {
    if (this.nearbyEventsContainer) {
      setTimeout(() => {
        const { scrollLeft } = this.nearbyEventsContainer.nativeElement;
        this.isAtStart = scrollLeft === 0;
      }, 800);
    }
  }

  public checkIsAtEnd() {
    if (this.nearbyEventsContainer) {
      setTimeout(() => {
        const { scrollWidth, scrollLeft, clientWidth } = this.nearbyEventsContainer.nativeElement;

        this.isAtEnd = scrollWidth - (scrollLeft + clientWidth) <= 10;
      }, 800);
    }
  }

  public goRight() {
    if (this.nearbyEventsMode === 'timeline') {
      this.timeline?.goRight();
      return;
    }

    this.nearbyEventsContainer.nativeElement.scrollLeft += this.getScrollWidth();

    this.checkIsAtStart();
    this.checkIsAtEnd();
  }

  public goLeft() {
    if (this.nearbyEventsMode === 'timeline') {
      this.timeline?.goLeft();
      return;
    }

    this.nearbyEventsContainer.nativeElement.scrollLeft -= this.getScrollWidth();

    this.checkIsAtStart();
    this.checkIsAtEnd();
  }

  getScrollWidth() {
    return this.nearbyEventsContainer.nativeElement.getBoundingClientRect().width + 16;
  }

  public toggleShowNearbyPlacesOnMap(status: MatCheckboxChange) {
    if (status.checked) {
      this.isShowingNearbyEventsOnMap = false;

      this.nearbyPlacesCom.nearByPlaces$.subscribe((places) => {
        const markers = places.map(
          (x) =>
            ({
              position: { lat: x.address.lat, lng: x.address.lng },
              options: {
                icon: 'https://iti-images.s3.amazonaws.com/imgs/place-48.png',
                zIndex: 2,
              },
              data: x,
            }) as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>,
        );

        this.keepPlaceMapMarker();
        this.mapMarkers = [...this.mapMarkers, ...markers];
        this.isShowingNearbyPlacesOnMap = true;
      });
    } else {
      this.keepPlaceMapMarker();
      this.isShowingNearbyPlacesOnMap = false;
    }
  }

  public markerClicked(marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>) {
    if (marker.data._id === this.place._id) {
      this.googleMap.closeInfoWindow();
      return;
    }
    if (this.isShowingNearbyEventsOnMap) {
      this.googleMap.showEventInfoWindow();
    } else {
      this.googleMap.showPlaceInfoWindow();
    }
  }

  public changeNearbyEventsMode() {
    if (this.nearbyEventsMode === 'normal') {
      this.nearbyEventsMode = 'timeline';
    } else {
      this.nearbyEventsMode = 'normal';
    }
  }

  getDisplayCategory(place: VisitorPlace) {
    return this.placeService.getDisplayCategory(place);
  }

  /**
   * handle browser back button click, close the place details page
   * only close the details page is not enough, it will always have other loading issues for the script embedding solution
   * so force reload the page for now
   * */
  // @HostListener('window:popstate', ['$event'])
  public hashChanged($event: Event) {
    $event.preventDefault();
    let url = window.location.href.split('imgoing-place')[0];
    url = url.endsWith('?') ? url.substring(0, url.length - 1) : url;
    window.location.href = url;
    this.close();
  }
}
